/** Senders currently held off by any suspension; shared so other parameter writers can respect it. */
const held = new WeakSet<RTCRtpSender>();

/** Pause encoding without detaching tracks from the peer's local stream.
 * Keeping sender.track intact is essential: peer-lite uses it to reconcile
 * tracks during renegotiation and to locate a sender when the camera changes.
 *
 * Other writers (`WebRtcOptimizationManager`, `SenderCodecPin`) rewrite
 * `encoding.active` on every quality decision. They consult `holds()` so a
 * suspended sender stays off, and `suspend()` re-asserts the pause if one of
 * them turned the sender back on anyway.
 */
export class VideoSenderSuspension {
  private paused = new Map<RTCRtpSender, boolean[]>();

  /** Whether some suspension currently holds this sender off. */
  static holds(sender: RTCRtpSender): boolean {
    return held.has(sender);
  }

  async suspend(pc: RTCPeerConnection | null): Promise<void> {
    try {
      for (const sender of pc?.getSenders() ?? []) {
        if (sender.track?.kind !== 'video') continue;
        const parameters = sender.getParameters() as RTCRtpSendParameters;
        if (!parameters.encodings?.length) throw new Error('Video sender is not negotiated');
        const previous = this.paused.get(sender);
        if (previous && parameters.encodings.every((encoding) => encoding.active === false)) continue;
        const active = previous ?? parameters.encodings.map((encoding) => encoding.active !== false);
        parameters.encodings.forEach((encoding) => {
          encoding.active = false;
        });
        await sender.setParameters(parameters);
        this.paused.set(sender, active);
        held.add(sender);
      }
    } catch (error) {
      await this.resume();
      throw error;
    }
  }

  async resume(): Promise<void> {
    for (const [sender, active] of this.paused) {
      held.delete(sender);
      const parameters = sender.getParameters() as RTCRtpSendParameters;
      parameters.encodings.forEach((encoding, index) => {
        encoding.active = active[index] ?? true;
      });
      await sender.setParameters(parameters);
      this.paused.delete(sender);
    }
  }
}
